import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { comboBox } from '@uswds/uswds/src/js/components'

/**
 * @param {ReactNode} children - One or more React components to be rendered
 * as options in the combo box.
 * @param {function} handleSelect - A function to set the state
 * of the selected option.
 * @param {string} selected - The value of the selected option.
 * @param {function} handleBlur - Runs on blur of combo box element.
 * @param {string} error - If validation in Profile component throws
 * an error then it is passed to combo box to render the error information.
 * @param {string} name - A string used for the name and id values of
 * the combo box.
 * @param {string} placeholder - A string used as a placeholder
 * in the combo box.
 * @param {string} label - The text content for the label tied to
 * the combo box.
 */
const ComboBox = ({
  children,
  handleSelect,
  selected = '',
  handleBlur = null,
  error = '',
  name,
  placeholder = '',
  label,
  autoComplete = true,
}) => {
  useEffect(() => {
    // The combo box was not rendering as a combo box without this line
    comboBox.init()
  }, [])

  useEffect(() => {
    const input = document.querySelector('.usa-combo-box__input')
    if (input) {
      if (error) {
        input.classList.add('usa-combo-box__input--error')
      }

      if (!error) {
        input.classList.remove('usa-combo-box__input--error')
      }
      input.setAttribute('aria-required', 'true')
    }
  })

  // Update the combo box display when selected value changes. This is required
  // to ensure that the combo box display is updated when the selected value
  // changes from higher up in the component hierarchy. This is a USWDS issue
  // that we can't step around because USWDS state and react state don't always
  // agree.
  useEffect(() => {
    // Update the USWDS combo box display input
    const comboBoxInput = document.querySelector('.usa-combo-box__input')
    if (comboBoxInput && comboBoxInput.value !== selected) {
      comboBoxInput.value = selected
    }
  }, [selected])

  return (
    <div
      className={classNames('usa-form-group', {
        'usa-form-group--error': error,
      })}
    >
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label
        className={`usa-label text-bold ${error ? 'usa-label--error' : ''}`}
        htmlFor={name}
      >
        {label}
        {error && (
          <div className="usa-error-message" id={`${name}-error-alert`}>
            {error}
          </div>
        )}
      </label>
      <div
        className="usa-combo-box"
        data-placeholder={placeholder}
        data-default-value={selected}
      >
        {/* eslint-disable-next-line jsx-a11y/no-onchange */}
        <select
          autoComplete={autoComplete ? 'on' : 'off'}
          className="usa-select"
          data-testid={`${name}-combobox`}
          aria-describedby={`${name}-error-alert`}
          name={name}
          id={name}
          onChange={(e) => {
            handleSelect(e.target.value)
            handleBlur && handleBlur(e)
          }}
          value={selected}
        >
          {children}
        </select>
      </div>
    </div>
  )
}

ComboBox.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  handleSelect: PropTypes.func.isRequired,
  selected: PropTypes.string,
  handleBlur: PropTypes.func,
  error: PropTypes.string,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string.isRequired,
  autoComplete: PropTypes.bool,
}

export default ComboBox
